import { useCallback, useRef, useState } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiInternshipFunction } from 'api/types/ApiInternshipFunction';
import { ApiJobFunction } from 'api/types/ApiJobFunction';
import { ApiListingApplicationListingType } from 'api/types/ApiListingApplicationListingType';
import { ApiRequestedAttachment } from 'api/types/ApiRequestedAttachment';
import { ApiSynonymMini } from 'api/types/ApiSynonymMini';
import { ApiOrgId } from 'api/types/ApiTypedId';
import { ApiVolopFunction } from 'api/types/ApiVolopFunction';
import { Box } from 'components/Box/Box';
import { Button } from 'components/Button/Button';
import { AnimatedCheckmark } from 'components/Icon/AnimatedCheckmark';
import { Icon } from 'components/Icon/Icon';
import { LegacyPrimaryButton } from 'components/LegacyButton/LegacyPrimaryButton';
import { LegacySecondaryButton } from 'components/LegacyButton/LegacySecondaryButton';
import { StyledNativeLinkWithRef } from 'components/Link/StyledNativeLinkWithRef';
import { BodySmall } from 'components/Text/BodySmall';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { useClipboard } from 'hooks/useClipboard';
import { useElementReachedEffect } from 'hooks/viewport/useElementReachedEffect';
import { ListingViewApplicationForm } from 'modules/listing/components/ListingView/ApplicationForm/ListingViewApplicationForm';
import { ListingViewSection } from 'modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingRichTextTruncatedHtmlWithLinks } from 'modules/listing/components/RichText/ListingRichTextTruncatedHtmlWithLinks';
import { useUserDashboardUserHasAppliedToListing } from 'modules/userDashboard/zustand-stores/userDashboardCurrentListingApplicationsStore';
import { colors, legacySpace } from 'theme/theme';
import { trackAnchorClicked } from 'utils/analytics/track/trackAnchorClicked';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from 'utils/constants/general/listingTypeHumanNameMapTitle';
import { OrgType } from 'utils/constants/general/orgTypes';
import { showModal } from 'zustand-stores/modalStore';
import { useUser } from 'zustand-stores/userEnvironmentStore';

type Props = {
  applyUrl: string | null;
  applyEmail: string | null;
  applyText: string | null;
  applyTextTruncated: string | null;
  hasAts: boolean;
  isPreview?: boolean;
  listingName: string;
  listingType: ApiListingApplicationListingType;
  listingId: string;
  orgId: ApiOrgId | undefined;
  orgType: OrgType | undefined;
  synonym: ApiSynonymMini | null;
  functions: ApiJobFunction[] | ApiInternshipFunction[] | ApiVolopFunction[];
  requestedAttachments: ApiRequestedAttachment[];
  requestedAttachmentsHash: string;
};

export function ListingViewApplySection({
  applyEmail,
  applyText,
  applyTextTruncated,
  applyUrl,
  functions,
  hasAts,
  isPreview = false,
  listingName,
  listingType,
  listingId,
  orgType,
  orgId,
  synonym,
  requestedAttachments,
  requestedAttachmentsHash,
}: Props) {
  const copyToClipboard = useClipboard();

  const atsRef = useRef<HTMLDivElement>(null);

  const [copied, setCopied] = useState(false);

  const { userHasAppliedToListing } =
    useUserDashboardUserHasAppliedToListing(listingId);

  useElementReachedEffect({
    ref: atsRef,
    onReach: useCallback(() => {
      trackEvent('Section View: Idealist ATS', {
        listing_type: listingType,
        listing_id: listingId,
        org_id: orgId || null,
        org_type: orgType || null,
        has_applied: userHasAppliedToListing,
      });
    }, [listingId, listingType, orgId, orgType, userHasAppliedToListing]),
  });

  const { user } = useUser();

  if (!applyEmail && !applyUrl && !applyText && !hasAts) return null;

  return (
    <ListingViewSection
      id="apply"
      title={
        /* TRANSLATORS: Job Listing Page, section title */
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-nested-ternary
        hasAts
          ? sprintf(
              listingType === 'EVENT'
                ? getText('Join This %(type)s')
                : getText('Apply to This %(type)s'),
              {
                type: LISTING_TYPE_HUMAN_NAME_MAP_TITLE[listingType],
              },
            )
          : listingType === 'EVENT'
            ? getText('How to Join')
            : getText('How to Apply')
      }
    >
      {hasAts ? (
        <div ref={atsRef}>
          {userHasAppliedToListing ? (
            <Box display="flex" alignItems="center">
              <Box
                width={24}
                height={24}
                display="flex"
                justifyContent="center"
                alignItems="center"
                mr="8px"
                borderRadius="50%"
                bg={colors.brandGreen}
              >
                <AnimatedCheckmark size={12} colorName="white" />
              </Box>

              <BodySmall>{getText('Thank you for applying!')}</BodySmall>
            </Box>
          ) : (
            <>
              {applyText && (
                <BodySmall>
                  <strong>{getText('Instructions')}:</strong>{' '}
                  <span // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: applyText,
                    }}
                  />
                </BodySmall>
              )}

              <Box mt={48}>
                <ListingViewApplicationForm
                  analyticsTitle="[main] Org listing page - how to apply section - apply"
                  usageContext="page"
                  isPreview={isPreview}
                  listingType={listingType}
                  listingId={listingId}
                  orgId={orgId}
                  orgType={orgType}
                  requestedAttachments={requestedAttachments}
                  requestedAttachmentsHash={requestedAttachmentsHash}
                />
              </Box>
            </>
          )}
        </div>
      ) : (
        <>
          {(applyUrl || applyEmail) && (
            <div
              style={{ display: 'flex', gap: legacySpace[3], flexWrap: 'wrap' }}
            >
              {applyUrl && (
                <LegacyPrimaryButton
                  component={StyledNativeLinkWithRef}
                  data-qa-id="apply-button"
                  href={applyUrl}
                  target="_blank"
                  $size="large"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    trackAnchorClicked(event, 'Apply To Listing', {
                      applied_via: 'external link',
                      listing_type: listingType,
                      listing_id: listingId,
                      org_id: orgId || null,
                      org_type: orgType || null,
                      has_ats: hasAts,
                    });

                    if (!user) {
                      showModal('EMAIL_ALERT', {
                        listingFunctions: functions,
                        orgId,
                        listingName,
                        listingType,
                        synonym,
                      });
                    }
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {getText('Go to Website')}
                    <Box display="flex" ml="8px">
                      <Icon name="arrow-up-right" size={20} />
                    </Box>
                  </Box>
                </LegacyPrimaryButton>
              )}

              {applyEmail && (
                <Tooltip
                  content={getText(
                    "You can't take this action in Preview Mode",
                  )}
                  triggerCondition={isPreview}
                  variant="filled"
                  placement="bottom-center"
                  size="large"
                >
                  <Box
                    cursor={isPreview ? 'not-allowed' : undefined}
                    style={
                      isPreview
                        ? {
                            pointerEvents: 'none',
                          }
                        : undefined
                    }
                  >
                    <Button
                      type="link"
                      variant={applyUrl ? 'secondary' : 'primary'}
                      data-qa-id="apply-button"
                      href={`mailto:${applyEmail}`}
                      target="_blank"
                      size="large"
                      icon={{ type: 'normalized', name: 'mail' }}
                      iconPosition="left"
                      onClick={() => {
                        trackClicked('Apply To Listing', {
                          applied_via: 'email',
                          listing_type: listingType,
                          listing_id: listingId,
                          org_id: orgId || null,
                          org_type: orgType || null,
                          has_ats: hasAts,
                        });

                        if (!user) {
                          showModal('EMAIL_ALERT', {
                            listingFunctions: functions,
                            orgId,
                            listingName,
                            listingType,
                            synonym,
                          });
                        }
                      }}
                    >
                      {getText('Send Email')}
                    </Button>
                  </Box>
                </Tooltip>
              )}

              {applyEmail && (
                <LegacySecondaryButton
                  $size="large"
                  onClick={() => {
                    trackClicked('Apply To Listing', {
                      applied_via: 'copied email',
                      listing_type: listingType,
                      listing_id: listingId,
                      org_id: orgId || null,
                      org_type: orgType || null,
                      has_ats: hasAts,
                    });

                    copyToClipboard(applyEmail).then(() => {
                      setCopied(true);
                      setTimeout(() => {
                        setCopied(false);
                      }, 5000);
                    });
                  }}
                  data-url={applyEmail}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box mr="8px" display="inline-flex">
                      <Icon name="copy" size={20} />
                    </Box>
                    {copied ? getText('Copied') : getText('Copy Email')}
                  </Box>
                </LegacySecondaryButton>
              )}
            </div>
          )}

          {applyText && (
            <div style={{ marginTop: 30 }}>
              <ListingRichTextTruncatedHtmlWithLinks
                showMoreLinkTitle={getText('Read more about how to apply')}
                showLessLinkTitle={getText('Read less about how to apply')}
                html={applyText}
                truncatedHtml={applyTextTruncated || ''}
                eventName="Apply To Listing"
                eventProperties={{
                  applied_via: 'external link',
                  listing_type: listingType,
                  listing_id: listingId,
                  org_id: orgId || null,
                  org_type: orgType || null,
                  has_ats: hasAts,
                }}
              />
            </div>
          )}
        </>
      )}
    </ListingViewSection>
  );
}
